/* ==========================================================================================
   Wellgard predictive search behaviour (Claude Design handoff "Predictive search 9e", 2026-10-11)
   ------------------------------------------------------------------------------------------
   Enhances Motion's <predictive-search> elements (snippets/predictive-search.liquid, marked edit)
   without replacing Motion's PredictiveSearch class in assets/theme.js: Motion still opens and
   closes the bar, debounces the typing, fetches the results and injects them. This script adds
   what the design needs on top, listening to the same events:

   - the empty state (open, nothing typed): the trending list already in the HTML, plus the four
     Best sellers fetched once on the first focus (/?section_id=custom-search-empty-state) and
     kept in sessionStorage for the visit; shown again when the field is cleared; shown only when
     a trending menu or a best-sellers collection is configured;
   - the clear button; the placeholder per breakpoint; the formatted review counts;
   - the no-results state: the cached Best sellers and, when there are no suggestions either,
     the trending list, copied into the slots the results section prints (ids re-suffixed);
   - keyboard and ARIA: aria-expanded and aria-controls on Motion's trigger(s) and the field
     (aria-controls follows the open popup, the empty state or the results), Down/Up/Home/End
     over every visible option with aria-activedescendant, Enter follows the active option,
     Escape closes (Motion) and the focus returns to the trigger that opened the bar;
   - the keyboard raised inside the tap on iOS (focus inside the trigger's click; the stylesheet
     makes the container's visibility flip instant on open so the field can take focus);
   - a request still in flight is aborted when the field is emptied or the bar closes;
   - the body lock on phones (position: fixed with the scroll offset saved and restored);
   - the /search page's own instance gets a unique results id, the clear button, the keyboard and
     the live region, but no empty state.

   Deferred after theme.js from layout/theme.liquid; idempotent per element; re-runs on the theme
   editor's section reload; does nothing on a page without the element.
   ========================================================================================== */
(function () {
  'use strict';
  if (!('querySelector' in document)) return;

  var PHONE = window.matchMedia ? window.matchMedia('(max-width: 589px)') : { matches: false };
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var EMPTY_SECTION = 'custom-search-empty-state';

  function formatCounts(scope) {
    var nodes = scope.querySelectorAll('[data-count]');
    for (var i = 0; i < nodes.length; i++) {
      var n = Number(nodes[i].getAttribute('data-count'));
      if (!isNaN(n)) nodes[i].textContent = '(' + n.toLocaleString('en-GB') + ')';
    }
  }

  function resuffixIds(scope, suffix) {
    var withId = scope.querySelectorAll('[id]');
    for (var i = 0; i < withId.length; i++) withId[i].id = withId[i].id + suffix;
    var labelled = scope.querySelectorAll('[aria-labelledby]');
    for (var j = 0; j < labelled.length; j++) {
      labelled[j].setAttribute('aria-labelledby', labelled[j].getAttribute('aria-labelledby') + suffix);
    }
    if (scope.hasAttribute && scope.hasAttribute('aria-labelledby')) {
      scope.setAttribute('aria-labelledby', scope.getAttribute('aria-labelledby') + suffix);
    }
  }

  function safeFocus(el) {
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch (err) { el.focus(); }
  }

  function enhance(root) {
    if (root.getAttribute('data-wg-search')) return;
    root.setAttribute('data-wg-search', 'on');

    var context = root.getAttribute('data-context') || 'header';
    var isHeader = context === 'header';
    var input = root.querySelector('input[type="search"]');
    var results = root.querySelector('.search__results');
    var clear = root.querySelector('.search__clear');
    var empty = root.querySelector('.search__empty');
    var best = root.querySelector('[data-best-sellers]');
    var bestGroup = root.querySelector('.search__best');
    var trending = root.querySelector('.search__trending');
    var live = root.querySelector('[data-search-live]');
    var container = isHeader ? document.querySelector('.site-header__search-container') : null;
    var triggers = isHeader ? document.querySelectorAll('.js-search-header') : [];
    var lastTrigger = triggers.length ? triggers[0] : null;
    if (!input || !results) return;

    /* Motion read #predictive-search once in its constructor and keeps the node, so the /search
       page's instance can take a unique id here without a core edit */
    if (!isHeader && results.id === 'predictive-search') results.id = 'predictive-search-page';
    if (empty && !empty.id) empty.id = 'predictive-search-empty';

    var isOpen = false;
    var bestHtml = null;       // null = not fetched yet; '' = nothing to show
    var bestFetching = false;
    var activeIndex = -1;
    var scrollY = 0;
    var collectionHandle = (bestGroup && bestGroup.getAttribute('data-collection')) || '';
    var cacheKey = 'wg-search-best:' + collectionHandle;

    /* ---- placeholder per breakpoint ---- */
    var phDesktop = input.getAttribute('data-placeholder-desktop') || input.getAttribute('placeholder') || '';
    var phPhone = input.getAttribute('data-placeholder-phone') || phDesktop;
    function applyPlaceholder() {
      input.setAttribute('placeholder', PHONE.matches ? phPhone : phDesktop);
    }
    applyPlaceholder();
    if (PHONE.addEventListener) PHONE.addEventListener('change', applyPlaceholder);
    else if (PHONE.addListener) PHONE.addListener(applyPlaceholder);

    /* ---- ARIA wiring: the combobox controls whichever popup is open; no aria-owns ---- */
    input.removeAttribute('aria-owns');
    function controls(id) {
      input.setAttribute('aria-controls', id);
      for (var i = 0; i < triggers.length; i++) triggers[i].setAttribute('aria-controls', id);
    }
    controls(results.id);
    for (var t = 0; t < triggers.length; t++) triggers[t].setAttribute('aria-expanded', 'false');
    if (clear) clear.hidden = !input.value;

    function announce(text) {
      if (!live) return;
      live.textContent = '';
      window.setTimeout(function () { live.textContent = text; }, 50);
    }

    function abortInFlight() {
      if (root.wgAbort) {
        try { root.wgAbort.abort(); } catch (err) { /* nothing to abort */ }
        root.wgAbort = null;
      }
    }

    /* ---- the empty state: shown only when it has something to show (a trending menu or best
       sellers); with neither configured yet the bar opens on its own, as Motion's did ---- */
    function emptyHasContent() {
      var hasTrending = !!(trending && trending.querySelector('[role="option"]'));
      var hasBest = !!(bestGroup && !bestGroup.hidden);
      return hasTrending || hasBest;
    }
    function showEmpty() {
      if (!empty) return;
      results.style.display = 'none';
      setActive(-1);
      if (!emptyHasContent()) {
        empty.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        controls(results.id);
        return;
      }
      empty.hidden = false;
      controls(empty.id);
      input.setAttribute('aria-expanded', 'true');
    }
    function hideEmpty() {
      if (!empty) return;
      empty.hidden = true;
      controls(results.id);
    }

    function readCache() {
      try { return window.sessionStorage.getItem(cacheKey); } catch (e) { return null; }
    }
    function writeCache(html) {
      try { window.sessionStorage.setItem(cacheKey, html); } catch (e) { /* private window or full */ }
    }
    function placeBest(html) {
      bestHtml = html;
      if (best) {
        best.innerHTML = html;
        formatCounts(best);
      }
      if (bestGroup) bestGroup.hidden = !html;
      fillNoResultsSlots();
      if (isOpen && !input.value.trim()) showEmpty();   /* the rows arrived while the empty state was due */
    }
    function fetchBest() {
      if (!best || bestHtml !== null || bestFetching) return;
      var cached = readCache();
      if (cached !== null) { placeBest(cached); return; }
      bestFetching = true;
      window.fetch(ROOT + '?section_id=' + EMPTY_SECTION, { credentials: 'same-origin' })
        .then(function (r) { if (!r.ok) throw new Error(String(r.status)); return r.text(); })
        .then(function (text) {
          var doc = new DOMParser().parseFromString(text, 'text/html');
          var section = doc.querySelector('#shopify-section-' + EMPTY_SECTION);
          var html = section ? section.innerHTML.trim() : '';
          if (section) writeCache(html);                 /* a real answer, even an empty one */
          placeBest(html);
        })
        .catch(function () { placeBest(''); })           /* a failed fetch is not cached */
        .then(function () { bestFetching = false; });
    }

    /* ---- the no-results state: fill the slots the section prints, ids re-suffixed ---- */
    function fillNoResultsSlots() {
      var panel = results.querySelector('[data-no-results]');
      if (!panel) return;
      var slot = panel.querySelector('[data-best-slot]');
      var list = panel.querySelector('[data-best-slot-list]');
      if (slot && list && bestHtml && !list.firstElementChild) {
        list.innerHTML = bestHtml;
        resuffixIds(list, '-nr');
        formatCounts(list);
        slot.hidden = false;
      }
      var tslot = panel.querySelector('[data-trending-slot]');
      if (tslot && trending && !tslot.firstElementChild) {
        var copy = trending.cloneNode(true);
        resuffixIds(copy, '-nr');
        tslot.appendChild(copy);
      }
    }

    /* ---- what Motion injects: watch the results container ---- */
    var observer = new MutationObserver(function () {
      if (!root.isConnected) return;
      if (!results.firstElementChild) {
        if (!empty || empty.hidden) {
          input.setAttribute('aria-expanded', 'false');
          setActive(-1);
        }
        return;
      }
      if (isHeader && !isOpen) {                       /* a late answer after the bar closed */
        results.innerHTML = '';
        results.style.display = 'none';
        return;
      }
      if (!input.value.trim()) {                        /* a late answer after the field was emptied */
        results.innerHTML = '';
        results.style.display = 'none';
        showEmpty();
        return;
      }
      hideEmpty();
      input.setAttribute('aria-expanded', 'true');
      formatCounts(results);
      setActive(-1);
      var panel = results.querySelector('.predictive-search-results');
      if (panel) {
        var q = Number(panel.getAttribute('data-query-count')) || 0;
        var p = Number(panel.getAttribute('data-product-count')) || 0;
        if (p === 0) {
          if (bestHtml === null) fetchBest();
          fillNoResultsSlots();
          var title = panel.querySelector('.search__none-title');
          announce(title ? title.textContent : 'No products found');
        } else {
          announce(q + (q === 1 ? ' suggestion, ' : ' suggestions, ') + p + (p === 1 ? ' product' : ' products'));
        }
      }
    });
    observer.observe(results, { childList: true });

    /* ---- open and close (Motion's events) ---- */
    function lockBody() {
      if (!PHONE.matches) return;
      scrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.top = (-scrollY) + 'px';
      document.body.classList.add('wg-search-lock');
    }
    function unlockBody() {
      if (!document.body.classList.contains('wg-search-lock')) return;
      document.body.classList.remove('wg-search-lock');
      document.body.style.top = '';
      window.scrollTo(0, scrollY);
    }

    document.addEventListener('predictive-search:open', function (e) {
      if (!root.isConnected) return;
      if (!e.detail || e.detail.context !== context) return;
      isOpen = true;
      for (var i = 0; i < triggers.length; i++) triggers[i].setAttribute('aria-expanded', 'true');
      lockBody();
      if (clear) clear.hidden = !input.value;
      var v = input.value.trim();
      if (v) {
        hideEmpty();
        /* Motion's close() emptied the results but kept the text: fetch them again */
        if (!results.firstElementChild && root.getAttribute('data-enabled') !== 'false' && typeof root.getSearchResults === 'function') {
          root.getSearchResults(v);
        }
      } else {
        showEmpty();
      }
      fetchBest();
    });

    document.addEventListener('predictive-search:close-all', function () {
      if (!root.isConnected) return;
      abortInFlight();
      input.setAttribute('aria-expanded', 'false');
      setActive(-1);
      if (!isOpen) return;
      isOpen = false;
      hideEmpty();
      unlockBody();
      for (var i = 0; i < triggers.length; i++) triggers[i].setAttribute('aria-expanded', 'false');
      if (lastTrigger && (root.contains(document.activeElement) || document.activeElement === document.body)) {
        safeFocus(lastTrigger);
      }
    });

    /* the keyboard rises inside the tap: focus the field in the trigger's own click, after making
       the container visible ourselves (Motion does the same in its handler, in either order; the
       stylesheet makes the visibility flip instant on open so the field can take focus now) */
    if (container) {
      for (var k = 0; k < triggers.length; k++) {
        triggers[k].addEventListener('click', function () {
          lastTrigger = this;
          container.classList.add('is-active');
          root.classList.add('is-active');
          safeFocus(input);
        });
      }
    }

    /* ---- first focus: the best sellers ---- */
    input.addEventListener('focus', function () { fetchBest(); });

    /* ---- typing: the clear button and the return to the empty state ---- */
    input.addEventListener('input', function () {
      setActive(-1);
      if (clear) clear.hidden = !input.value;
      if (!input.value.trim()) {
        abortInFlight();
        results.innerHTML = '';
        results.style.display = 'none';
        if (isOpen || !isHeader) showEmpty();
        if (!empty || empty.hidden) input.setAttribute('aria-expanded', 'false');
      }
    });

    if (clear) {
      clear.addEventListener('click', function () {
        input.value = '';
        clear.hidden = true;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        safeFocus(input);
      });
    }

    /* ---- keyboard: the options in DOM order across the empty state and the results ---- */
    function visibleOptions() {
      var scope = (empty && !empty.hidden) ? empty : results;
      var all = scope.querySelectorAll('[role="option"]');
      var out = [];
      for (var i = 0; i < all.length; i++) {
        if (all[i].offsetParent !== null) out.push(all[i]);
      }
      return out;
    }
    function setActive(index) {
      var options = visibleOptions();
      for (var i = 0; i < options.length; i++) options[i].classList.remove('is-active-option');
      activeIndex = index;
      if (index < 0 || index >= options.length) {
        input.removeAttribute('aria-activedescendant');
        return;
      }
      var el = options[index];
      el.classList.add('is-active-option');
      if (el.id) input.setAttribute('aria-activedescendant', el.id);
      if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }
    input.addEventListener('keydown', function (e) {
      var key = e.key;
      var options = visibleOptions();
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Home' || key === 'End') {
        if (!options.length) return;
        e.preventDefault();
        var next = activeIndex;
        if (key === 'ArrowDown') next = activeIndex + 1 >= options.length ? 0 : activeIndex + 1;
        if (key === 'ArrowUp') next = activeIndex - 1 < 0 ? options.length - 1 : activeIndex - 1;
        if (key === 'Home') next = 0;
        if (key === 'End') next = options.length - 1;
        setActive(next);
        return;
      }
      if (key === 'Enter' && activeIndex >= 0 && activeIndex < options.length) {
        var link = options[activeIndex].querySelector('a');
        if (link && link.href) {
          e.preventDefault();
          window.location.href = link.href;
        }
      }
    });
  }

  function init() {
    var roots = document.querySelectorAll('predictive-search');
    for (var i = 0; i < roots.length; i++) enhance(roots[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
